/**
 * Risk-limit editor (mandate §1) — composed dialog behind the expanded risk card's "Edit limit".
 *
 * One input, one law: the value saves through `prepare.riskProfile.update` and the card re-renders from the
 * SERVER's answer. A loosening does not appear on the card early — the response queues it (ADR-014) and the
 * card carries the queued note until the delay elapses.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:360px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const field = css(
  'width:100%;padding:9px 12px;border-radius:10px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:12.5px;font-variant-numeric:tabular-nums',
);
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const primaryBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent);background:color-mix(in srgb, var(--slate-fill) 10%, transparent);color:var(--slate);cursor:pointer',
);

export function RiskValueEditor({ m }: { m: Partial<ViewModel> }) {
  const e = m.riskEditorOpen;
  if (!e) return null;
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={`Edit ${e.name}`}>
      <div style={sheet}>
        <h2 style={css('font-size:13px;font-weight:600;margin:0 0 6px')}>{e.name}</h2>
        <p
          id="pf-risk-editor-note"
          style={css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 12px')}
        >
          Tightening applies immediately. Loosening is queued for the refractory delay and the card says so —
          the current limit keeps enforcing until then.
        </p>
        <input
          style={field}
          aria-label={`${e.name} value`}
          aria-describedby="pf-risk-editor-note"
          value={e.value}
          onChange={(ev) => m.riskEditorSet?.(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') m.riskEditorSave?.();
          }}
        />
        <div style={css('display:flex;gap:8px;justify-content:flex-end;margin-top:14px')}>
          <button style={ghostBtn} onClick={m.riskEditorCancel}>
            Cancel
          </button>
          <button style={primaryBtn} onClick={m.riskEditorSave} aria-label="Save the limit">
            Save limit
          </button>
        </div>
      </div>
    </div>
  );
}
